/**
 * Confirm enqueues a Xero push. It never performs the POST.
 * Hold and an overridden money mismatch never enqueue.
 */

export const MONEY_OVERRIDE_BLOCK =
  'Money mismatch was overridden — not sent to Xero automatically. Send from the receipt if the figures are right.';

export type ReceiptPushDisplayState =
  | 'confirmed'
  | 'queued'
  | 'action_needed'
  | 'sent_to_accounts'
  | 'unknown'
  | 'failed';

export const PUSH_DISPLAY_LABEL: Record<ReceiptPushDisplayState, string> = {
  confirmed: 'Confirmed',
  queued: 'Queued for Xero',
  action_needed: 'Needs you',
  sent_to_accounts: 'Sent to accounts',
  unknown: 'Xero outcome unknown',
  failed: 'Xero send failed',
};
export type EnqueueDecision = {
  enqueue: boolean;
  blockReason: string | null;
};

export function enqueueDecision(receipt: {
  status: string;
  held: boolean;
  held_reason: string | null;
  money_reconcile_overridden: boolean;
  xero_bill_id: string | null;
  xero_push_outcome?: string | null;
}): EnqueueDecision {
  if (receipt.xero_bill_id || receipt.status === 'sent_to_accounts') {
    return { enqueue: false, blockReason: null };
  }
  if (
    receipt.xero_push_outcome === 'unknown' ||
    receipt.xero_push_outcome === 'failed' ||
    receipt.xero_push_outcome === 'posted'
  ) {
    return { enqueue: false, blockReason: null };
  }
  if (receipt.held) {
    const reason = receipt.held_reason?.trim();
    return {
      enqueue: false,
      blockReason: reason ? `Held — ${reason}` : 'Held — a reason is required',
    };
  }
  if (receipt.money_reconcile_overridden) {
    return { enqueue: false, blockReason: MONEY_OVERRIDE_BLOCK };
  }
  return { enqueue: true, blockReason: null };
}

/**
 * List / detail label.
 * queued = waiting on connectivity or the next flush — will happen by itself.
 * action_needed = waiting on Jack (hold, override, contact, account, tax).
 * A hold outranks every push state, including a receipt that already has a bill.
 */
export function receiptPushDisplayState(receipt: {
  status: string;
  xero_bill_id?: string | null;
  xero_push_outcome?: string | null;
  held?: boolean | null;
  money_reconcile_overridden?: boolean | null;
}): ReceiptPushDisplayState {
  if (receipt.held) return 'action_needed';
  const outcome = receipt.xero_push_outcome ?? 'idle';
  if (outcome === 'unknown') return 'unknown';
  if (outcome === 'failed') return 'failed';
  if (receipt.status === 'sent_to_accounts' || receipt.xero_bill_id) {
    return 'sent_to_accounts';
  }
  if (receipt.money_reconcile_overridden || outcome === 'blocked') {
    return 'action_needed';
  }
  if (outcome === 'queued' || outcome === 'in_flight') return 'queued';
  return 'confirmed';
}
